import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
    ApplyPipe,
    IconButtonComponent,
    MenuComponent,
    MenuItemDecoratorDirective,
    SeparatorComponent,
} from '@interacta-shared/ui';
import { IconComponent } from '@interacta-shared/ui-icons';
import { InteractaIcon, isDefined } from '@interacta-shared/util';
import { TranslateModule } from '@ngx-translate/core';
import {
    MenuActionButtonItem,
    MenuActionButtonItemLink,
} from '../../models/menu-actions.model';

@Component({
    selector: 'interacta-menu-actions-button',
    imports: [
        MenuComponent,
        MenuItemDecoratorDirective,
        IconButtonComponent,
        SeparatorComponent,
        IconComponent,
        CdkOverlayOrigin,
        TranslateModule,
        RouterLink,
        ApplyPipe,
        NgTemplateOutlet,
    ],
    templateUrl: './menu-actions-button.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[class.hidden]': 'hideButton()' },
})
export class MenuActionsButtonComponent<T> {
    readonly items = input.required<MenuActionButtonItem<T>[]>();
    readonly args = input.required<T>();
    readonly tooltip = input<string>('');

    readonly menuIsOpen = signal(false);

    readonly itemsFiltered = computed(() =>
        this.items().filter((item) =>
            item.condition ? item.condition(this.args()) : true,
        ),
    );
    readonly hideButton = computed(() => !this.itemsFiltered().length);

    separator({
        args,
        separator,
    }: {
        args: T;
        separator: MenuActionButtonItem<T>['separator'];
    }): boolean {
        if (isDefined(separator)) {
            return typeof separator === 'boolean' ? separator : separator(args);
        }
        return false;
    }

    link({
        args,
        link,
    }: {
        args: T;
        link: MenuActionButtonItemLink<T>['link'];
    }): RouterLink['routerLink'] {
        return typeof link === 'function' ? link(args) : link;
    }

    queryParams({
        row,
        params,
    }: {
        row: T;
        params: MenuActionButtonItemLink<T>['queryParams'];
    }): RouterLink['queryParams'] {
        return typeof params === 'function' ? params(row) : params;
    }

    label({
        row,
        label,
    }: {
        row: T;
        label: MenuActionButtonItemLink<T>['label'];
    }): string {
        return typeof label === 'function' ? label(row) : label;
    }

    icon({
        row,
        icon,
    }: {
        row: T;
        icon: MenuActionButtonItemLink<T>['icon'];
    }): InteractaIcon {
        return typeof icon === 'function' ? icon(row) : icon;
    }
}
